import { AppliedPromotionsType, CheckoutLines, Price } from "@/common/lib/types";
import { giftTypes, promotionType } from "@/apiUtils/constants";
import { isCheckoutGiftLine } from "@/utils/checkoutGiftLine";

/** Prefer BXGY trigger promo on a line (not the free-gift child). */
export function resolvePrimaryAppliedPromotion(
  appliedPromotions?: AppliedPromotionsType[] | null,
): AppliedPromotionsType | undefined {
  const promos = appliedPromotions ?? [];
  if (!promos.length) {
    return undefined;
  }

  return (
    promos.find((p) => p.promotion?.type === promotionType.BUYXGETY && p.giftType !== giftTypes.FREE_GIFT) ??
    promos.find((p) => p.giftType !== giftTypes.FREE_GIFT) ??
    promos[0]
  );
}

/** First storefront-visible promotion badge name (respects showBadgeInStorefront). */
export function getPromotionBadgeNameFromPromotions(
  promotions?: AppliedPromotionsType[] | null,
  language: string = "en",
): string {
  const promo = (promotions || []).find(
    (p) =>
      (p?.promotion?.showBadgeInStorefront ?? true) &&
      p?.giftType !== giftTypes.FREE_GIFT &&
      p?.promotion?.type !== "ORDER" &&
      (!p?.ineligibilityReason || p.ineligibilityReason === "ELIGIBLE"),
  );
  if (!promo) return "";
  return language === "ar"
    ? promo.promotion?.translation?.name || promo.translation?.name || promo.promotion?.name || promo.name || ""
    : promo.promotion?.name || promo.name || "";
}

export type PromotionLimitError = {
  name: string;
  reason: string;
  isGlobal: boolean;
  isPerUser: boolean;
  isPerCode: boolean;
};

/** Collect cart promotion limit alerts for all per-user / per-code / global scenarios. */
export const buildPromotionLimitErrors = (
  lines: CheckoutLines[] | null | undefined,
  voucherCode?: string | null,
): PromotionLimitError[] => {
  const errors: PromotionLimitError[] = [];
  const processedKeys = new Set<string>();

  (lines ?? []).forEach((line) => {
    const allPromos = [
      ...(line?.appliedPromotions || []),
      ...(line?.variant?.availablePromotions || []),
    ];

    allPromos.forEach((promo: AppliedPromotionsType) => {
      const isPerUser = !!(
        promo?.isPerUserLimitReached || promo?.ineligibilityReason === "PER_USER_LIMIT_REACHED"
      );
      const isGlobal = !!(
        promo?.isGlobalLimitReached || promo?.ineligibilityReason === "GLOBAL_LIMIT_REACHED"
      );
      const isPerCode = !!(
        promo?.isPerCodeLimitReached || promo?.ineligibilityReason === "PER_CODE_LIMIT_REACHED"
      );
      const isExhausted = isPerUser || isGlobal || isPerCode;

      if (!isExhausted) return;

      const promoName =
        promo?.promotion?.translation?.name || promo?.promotion?.name || promo?.name || "";

      if (isPerUser) {
        const dedupKey = `per-user-${promoName || promo?.id || "unknown"}`;
        if (!processedKeys.has(dedupKey)) {
          processedKeys.add(dedupKey);
          errors.push({
            name: promoName || "",
            reason: promo?.ineligibilityReason || "PER_USER_LIMIT_REACHED",
            isGlobal: false,
            isPerUser: true,
            isPerCode: false,
          });
        }
        return;
      }

      if (isPerCode) {
        const dedupKey = `per-code-${promoName || promo?.id || voucherCode || "unknown"}`;
        if (!processedKeys.has(dedupKey)) {
          processedKeys.add(dedupKey);
          errors.push({
            name: promoName || "",
            reason: promo?.ineligibilityReason || "PER_CODE_LIMIT_REACHED",
            isGlobal: false,
            isPerUser: false,
            isPerCode: true,
          });
        }
        return;
      }

      const dedupKey = promoName || promo?.id || promo?.ineligibilityReason || "unknown";

      if (!processedKeys.has(dedupKey)) {
        processedKeys.add(dedupKey);
        errors.push({
          name: promoName || "",
          reason: promo?.ineligibilityReason || "LIMIT_REACHED",
          isGlobal,
          isPerUser: false,
          isPerCode: false,
        });
      }
    });
  });

  return errors;
};

/** Per-code limit only blocks the voucher/coupon — not catalogue promotion display. */
export const isPerCodeLimitReached = (promo?: AppliedPromotionsType | null): boolean => {
  if (!promo) return false;
  return !!(promo.isPerCodeLimitReached || promo.ineligibilityReason === "PER_CODE_LIMIT_REACHED");
};

/** Catalogue promotion blocked because a coupon is already applied (per-user limit not reached). */
export const isPromotionBlockedByCoupon = (
  promo?: AppliedPromotionsType | null,
  hasVoucherCode?: boolean
): boolean => {
  if (!promo || !hasVoucherCode) return false;
  return isPerCodeLimitReached(promo) && !promo.isPerUserLimitReached && promo.ineligibilityReason !== "PER_USER_LIMIT_REACHED";
};

/** Per-user or global limit — hide promo badge and sale pricing on the product card. */
export const isPromotionLimitReached = (promo?: AppliedPromotionsType | null): boolean => {
  if (!promo) return false;
  return !!(
    promo.isPerUserLimitReached ||
    promo.isGlobalLimitReached ||
    promo.ineligibilityReason === "PER_USER_LIMIT_REACHED" ||
    promo.ineligibilityReason === "GLOBAL_LIMIT_REACHED"
  );
};

export const getLinePromotions = (
  appliedPromotions?: AppliedPromotionsType[] | null,
  availablePromotions?: AppliedPromotionsType[] | null,
): AppliedPromotionsType[] => [...(appliedPromotions || []), ...(availablePromotions || [])];

export const isAnyPromotionLimitReached = (
  appliedPromotions?: AppliedPromotionsType[] | null,
  availablePromotions?: AppliedPromotionsType[] | null,
): boolean => getLinePromotions(appliedPromotions, availablePromotions).some(isPromotionLimitReached);

export const isAnyPerCodeLimitReached = (
  appliedPromotions?: AppliedPromotionsType[] | null,
  availablePromotions?: AppliedPromotionsType[] | null,
): boolean => getLinePromotions(appliedPromotions, availablePromotions).some(isPerCodeLimitReached);

/**
 * True when any checkout line reports a hard promotion usage limit (per-user / global).
 * Per-code / coupon conflict must NOT count here — that would hide BXGY free gifts while a voucher is applied.
 */
export const isCheckoutPromotionLimitReached = (
  lines?: CheckoutLines[] | null,
): boolean =>
  (lines ?? []).some((line) =>
    isAnyPromotionLimitReached(line.appliedPromotions, line.variant?.availablePromotions),
  );

/**
 * Paid cart row — must stay visible when promotion limits / coupon conflicts hide free gifts.
 * Charge is based on checkout line prices only (never variant catalogue price alone for gift flags).
 *
 * Saleor BXGY (`buy_get_gift_flag` / isBuyxGetyGift) zeros totalPrice. AE VAT can leave
 * residue on unitPrice only — that must not count as paid merchandise.
 */
export const isPaidCheckoutMerchandiseLine = (line?: CheckoutLines | null): boolean => {
  if (!line?.id || line.parent?.id) {
    return false;
  }

  // Explicit gift flags only — do not use appliedPromotions.giftType FREE_GIFT here.
  // BXGY buy-X parents carry that giftType as reward metadata while still being charged.
  if (line.isGift || line.isBuyxGetyGift || line.buyGetGiftFlag) {
    return false;
  }

  const quantity = line?.quantity || 1;
  const lineTotal = getCheckoutPriceAmount(line?.totalPrice);
  const unit = getCheckoutPriceAmount(line?.unitPrice) ?? 0;
  const hasExplicitZeroTotal = lineTotal != null && lineTotal <= 0.01;
  const hasPositiveTotal = lineTotal != null && lineTotal > 0.01;
  const hasCharge = hasExplicitZeroTotal ? false : hasPositiveTotal || unit * quantity > 0.01;

  if (hasCharge) {
    return true;
  }

  // Limit-exhausted BXGY parents can briefly lose line money while catalogue price remains —
  // still treat as paid merchandise so PER_USER_LIMIT does not hide the product card.
  if (line.isGift === false && line.isBuyxGetyGift === false) {
    const catalog =
      line.variant?.pricing?.price?.gross?.amount ??
      line.variant?.pricing?.priceUndiscounted?.gross?.amount ??
      line.variant?.pricing?.costPrice?.gross?.amount ??
      0;
    if (catalog > 0.01) {
      return true;
    }
  }

  return false;
};

export const isAnyPromotionBlockedByCoupon = (
  appliedPromotions?: AppliedPromotionsType[] | null,
  availablePromotions?: AppliedPromotionsType[] | null,
  hasVoucherCode?: boolean,
): boolean =>
  getLinePromotions(appliedPromotions, availablePromotions).some((promo) =>
    isPromotionBlockedByCoupon(promo, hasVoucherCode),
  );

/**
 * Catalogue promo sale should fall back to retail when per-user/global limits hit.
 * Per-code + voucher is NOT included — the coupon still needs strikethrough (list − voucher).
 */
export const shouldUseUndiscountedPromotionPrice = (
  appliedPromotions?: AppliedPromotionsType[] | null,
  availablePromotions?: AppliedPromotionsType[] | null,
  _hasVoucherCode?: boolean,
): boolean =>
  getLinePromotions(appliedPromotions, availablePromotions).some((promo) => {
    const isPerUser = !!(
      promo?.isPerUserLimitReached || promo?.ineligibilityReason === "PER_USER_LIMIT_REACHED"
    );
    const isGlobal = !!(
      promo?.isGlobalLimitReached || promo?.ineligibilityReason === "GLOBAL_LIMIT_REACHED"
    );
    return isPerUser || isGlobal;
  });

/** Read checkout Money or TaxedMoney amount from line-level price fields. */
export const getCheckoutPriceAmount = (
  price?: Price | { amount?: number | null; gross?: { amount?: number | null } } | null,
): number | undefined => {
  if (!price) return undefined;
  const gross = "gross" in price ? price.gross?.amount : undefined;
  if (gross != null) return gross;
  if ("amount" in price && price.amount != null) return price.amount;
  return undefined;
};

/** Resolve variant TaxedMoney to a display gross amount (handles net + tax when gross is net-only). */
export const resolveTaxedGrossAmount = (price?: Price | null): number => {
  if (!price) return 0;
  const gross = price.gross?.amount;
  const net = price.net?.amount;
  const tax = price.tax?.amount ?? 0;

  if (gross != null && net != null && tax > 0) {
    if (Math.abs(gross - (net + tax)) <= 0.02) return gross;
    if (Math.abs(gross - net) <= 0.02) return net + tax;
    return gross;
  }
  if (gross != null) return gross;
  if (net != null) return net + tax;
  return 0;
};

/**
 * True when `candidate` is `catalogList` plus a known VAT rate (AE 5% / EG 14% / KSA 15%).
 * Used to detect checkout net (540) vs the VAT-inclusive PDP / dashboard price (567).
 */
export const looksLikeVatInflatedGross = (catalogList: number, candidate: number): boolean => {
  if (catalogList <= 0.01 || candidate <= catalogList + 0.01) {
    return false;
  }
  return [0.05, 0.14, 0.15].some(
    (rate) => Math.abs(candidate - catalogList * (1 + rate)) <= 0.05,
  );
};

/** True when the cheaper amount is only the net of a known VAT-inclusive gross (AE 5% / EG 14% / KSA 15%). */
export const isVatOnlyPriceDifference = (netOrSale: number, grossOrList: number): boolean =>
  looksLikeVatInflatedGross(netOrSale, grossOrList);

/** List vs sale strikethrough: real markdown only. VAT-inclusive vs net is not a discount. */
export const isPromotionalCartPrice = (listPrice: number, salePrice: number): boolean =>
  listPrice > salePrice + 0.01 && !isVatOnlyPriceDifference(salePrice, listPrice);

/**
 * Checkout AE exposes net catalog (540) + tax (27). PDP, dashboard, and the payable
 * order use the VAT-inclusive amount (567). Add tax only when it is a VAT pair —
 * already-inclusive 567 + 27 is not, so order success stays on 567.
 */
export const toVatInclusiveCatalogAmount = (
  amount?: number | null,
  tax?: number | null,
): number => {
  if (amount == null || amount <= 0.01) {
    return amount ?? 0;
  }
  if (tax == null || tax <= 0.01) {
    return amount;
  }
  const grossed = amount + tax;
  if (looksLikeVatInflatedGross(amount, grossed)) {
    return grossed;
  }
  return amount;
};

/** When two amounts are a net / VAT-gross pair, keep the customer-facing gross. */
export const preferVatInclusiveAmount = (a: number, b: number): number => {
  if (a > 0.01 && looksLikeVatInflatedGross(b, a)) {
    return a;
  }
  if (b > 0.01 && looksLikeVatInflatedGross(a, b)) {
    return b;
  }
  return Math.max(a, b);
};

/**
 * Apply paid-line tax ratio only when `catalogueAmount` looks net (closer to paidNet than paidGross).
 * Tax-inclusive channels (e.g. TH) expose catalogue gross ≈ paidGross — never add VAT again
 * (882 with 5% tax must stay 882, not become 926.10).
 * AE tax-exclusive: catalog 540 + tax 27 is the PDP price 567.
 */
const applyTaxRatioIfNetLike = (
  catalogueAmount: number,
  paidGross: number,
  paidTax: number,
): number => {
  if (catalogueAmount <= 0 || paidGross <= 0 || paidTax <= 0) {
    return catalogueAmount;
  }
  const paidNet = paidGross - paidTax;
  if (paidNet <= 0) return catalogueAmount;

  // Already tax-inclusive (matches paid gross) — do not double VAT.
  if (Math.abs(catalogueAmount - paidGross) <= 0.02) {
    return catalogueAmount;
  }

  // Catalog list + channel VAT (540 → 567). Show the VAT-inclusive PDP price.
  if (looksLikeVatInflatedGross(catalogueAmount, catalogueAmount + paidTax)) {
    return catalogueAmount + paidTax;
  }
  if (looksLikeVatInflatedGross(catalogueAmount, paidGross)) {
    return paidGross;
  }

  const nearerToNet =
    Math.abs(catalogueAmount - paidNet) <= Math.abs(catalogueAmount - paidGross);
  if (nearerToNet || Math.abs(catalogueAmount - paidNet) <= 0.02) {
    return catalogueAmount * (1 + paidTax / paidNet);
  }

  return catalogueAmount;
};

/**
 * Saleor MLC-1986 on tax-exclusive AE: tax the undiscounted list, then subtract the voucher
 * from net AND gross. Catalog listings stay at the pre-tax amount (840 / 380), so line.gross
 * understates the coupon (782 vs 740). Negative discount.tax is the API signal.
 */
export function getLineVoucherNetDiscountAmount(line?: CheckoutLines | null): number {
  const discount = line?.variant?.pricing?.discount;
  const gross = discount?.gross?.amount ?? 0;
  const tax = discount?.tax?.amount ?? 0;
  if (tax < -0.01 && gross > 0.01) {
    return gross - tax;
  }
  return 0;
}

export function hasVoucherTaxDistortedGross(
  voucherAmount: number,
  retailSubtotal: number,
  paidGross: number,
): boolean {
  if (voucherAmount <= 0.01 || retailSubtotal <= 0.01) {
    return false;
  }
  const expectedPaid = retailSubtotal - voucherAmount;
  if (expectedPaid < -0.01) {
    return false;
  }
  return paidGross > expectedPaid + 0.01;
}

/** Catalog list − coupon when AE VAT put tax back on gross; otherwise the API paid amount. */
export function resolveCatalogVoucherPaidTotal(
  retailSubtotal: number,
  voucherAmount: number,
  paidGross: number,
): number {
  const expected = Math.max(0, retailSubtotal - Math.max(0, voucherAmount));
  // Missing/zero line paid (gift filtered, stale lines) must not blank Order Total — use list − voucher.
  if (paidGross <= 0.01) {
    return expected;
  }
  if (hasVoucherTaxDistortedGross(voucherAmount, retailSubtotal, paidGross)) {
    return expected;
  }
  return Math.max(0, paidGross);
}

export type CatalogVoucherPaidOptions = {
  undiscountedSubtotalGross?: number | null;
  checkoutMerchandiseLineCount?: number;
  paidSubtotalGross?: number | null;
  voucherDiscountAmount?: number;
  checkoutLines?: CheckoutLines[] | null;
};

/**
 * Paid unit to show after a voucher: catalog list − coupon share.
 * AE: 840 − 100 = 740 (not API gross 782). SA: matches API already.
 * BXGY free gifts: 0 even if unitPrice still has VAT residue.
 */
export function getLineCatalogVoucherPaidUnitPrice(
  line?: CheckoutLines | null,
  options?: CatalogVoucherPaidOptions,
): number | null {
  if (!line) {
    return null;
  }

  if (isCheckoutGiftLine(line) || line.isBuyxGetyGift || line.isGift || line.buyGetGiftFlag) {
    return 0;
  }

  const retail = getLineRetailUnitPrice(line, options);
  const voucherAmount = options?.voucherDiscountAmount ?? 0;
  const fromPricing = getLineVoucherNetDiscountAmount(line);

  const allocatable = (options?.checkoutLines ?? []).filter(
    (item) => isPaidCheckoutMerchandiseLine(item) && !isCheckoutGiftLine(item),
  );
  const nonCataloguePaid = allocatable.filter(
    (item) => !getEligibleCataloguePromotionForCard(item.appliedPromotions),
  );
  // Coupon displays on the non-catalogue paid line, not split onto a catalogue card.
  const isSoleCouponDisplayLine =
    allocatable.length <= 1 ||
    (nonCataloguePaid.length <= 1 &&
      (nonCataloguePaid.length === 0 || nonCataloguePaid.some((item) => item.id === line.id)));

  const preferApiPaidIfHigher = (patched: number): number => {
    const apiPaid = getCheckoutPriceAmount(line?.unitPrice) ?? 0;
    // Dashboard / payable uses Saleor paid gross (488.74), not list − coupon (467).
    if (apiPaid > patched + 0.01) {
      return apiPaid;
    }
    return patched;
  };

  if (fromPricing > 0.01 && retail > 0.01) {
    const catalogDiscount =
      isSoleCouponDisplayLine && voucherAmount > fromPricing + 0.01 ? voucherAmount : fromPricing;
    return preferApiPaidIfHigher(Math.max(0, retail - catalogDiscount));
  }

  if (voucherAmount <= 0.01 || retail <= 0.01) {
    return null;
  }

  if (allocatable.length > 0 && !allocatable.some((item) => item.id === line.id)) {
    return null;
  }

  // Prefer Saleor's allocated unit price (proportional voucher split, etc.).
  // Do not replace it with an equal per-unit split (that showed 600 instead of API 603.24).
  const apiPaid = getCheckoutPriceAmount(line?.unitPrice) ?? 0;
  if (apiPaid > 0.01) {
    return apiPaid;
  }

  if (isSoleCouponDisplayLine) {
    return preferApiPaidIfHigher(Math.max(0, retail - voucherAmount));
  }

  const units =
    allocatable.reduce((sum, item) => sum + (item.quantity || 1), 0) || (line.quantity || 1);
  return Math.max(0, retail - voucherAmount / units);
}

/** Line undiscounted fields from Saleor often expose a plain `amount` (net) — only trust TaxedMoney gross. */
const getLineUndiscountedTaxedGrossAmount = (
  price?: Price | { amount?: number | null; gross?: { amount?: number | null } } | null,
): number | undefined => {
  if (!price || !("gross" in price) || price.gross?.amount == null) {
    return undefined;
  }
  return resolveTaxedGrossAmount(price as Price);
};

/** Gross-up a net-like checkout unit price using line TaxedMoney or variant pricing tax ratio. */
const grossUpCheckoutLineUnitPrice = (
  cartItem?: CheckoutLines | null,
  lineUnit?: number | null,
): number => {
  if (lineUnit == null || lineUnit <= 0) return lineUnit ?? 0;

  const fromTaxed = resolveTaxedGrossAmount(cartItem?.undiscountedUnitPrice as Price);
  if (fromTaxed > lineUnit + 0.02) return fromTaxed;

  const pricing = cartItem?.variant?.pricing;
  // AE voucher: discount.tax is negative — keep catalog list (840), do not invent 882.
  if ((pricing?.discount?.tax?.amount ?? 0) < -0.01) {
    return lineUnit;
  }
  // Prefer Saleor TaxedMoney (gross/net/tax) — never invent VAT on top of tax-inclusive amounts.
  const fromVariantUndiscounted = resolveTaxedGrossAmount(pricing?.priceUndiscounted);
  if (fromVariantUndiscounted > 0.01) {
    // Keep the higher of resolved catalogue vs line money (lens/options may exceed catalogue).
    return Math.max(fromVariantUndiscounted, lineUnit);
  }

  const undiscountedNetLike = pricing?.priceUndiscounted?.gross?.amount;
  const paidGross = pricing?.price?.gross?.amount;
  const paidTax = pricing?.price?.tax?.amount ?? 0;

  if (undiscountedNetLike != null && paidGross != null && paidTax > 0) {
    if (Math.abs(undiscountedNetLike - lineUnit) <= 0.02) {
      const retailGross = applyTaxRatioIfNetLike(undiscountedNetLike, paidGross, paidTax);
      if (retailGross > lineUnit + 0.02) return retailGross;
    }
  }

  return lineUnit;
};

/** Full retail unit price for a cart line — matches checkout undiscounted subtotal gross. */
export const getLineRetailUnitPrice = (
  cartItem?: CheckoutLines | null,
  options?: {
    undiscountedSubtotalGross?: number | null;
    checkoutMerchandiseLineCount?: number;
    paidSubtotalGross?: number | null;
  },
): number => {
  const quantity = cartItem?.quantity || 1;
  const { undiscountedSubtotalGross, checkoutMerchandiseLineCount, paidSubtotalGross } = options ?? {};

  const paidTax = cartItem?.variant?.pricing?.price?.tax?.amount ?? 0;
  const fromCost = cartItem?.variant?.pricing?.costPrice?.gross?.amount;
  // PDP / dashboard list (567 Incl VAT). Checkout often omits costPrice — fall through to tax add.
  if (fromCost != null && fromCost > 0.01) {
    return fromCost;
  }

  const rawFromLineUnit =
    getLineUndiscountedTaxedGrossAmount(cartItem?.undiscountedUnitPrice) ??
    getCheckoutPriceAmount(cartItem?.undiscountedUnitPrice);
  const fromLineUnit =
    rawFromLineUnit != null ? toVatInclusiveCatalogAmount(rawFromLineUnit, paidTax) : undefined;
  const currentUnit = getCheckoutPriceAmount(cartItem?.unitPrice);
  const rawCatalogList = resolveTaxedGrossAmount(cartItem?.variant?.pricing?.priceUndiscounted);
  const catalogList = toVatInclusiveCatalogAmount(rawCatalogList, paidTax);
  // Checkout net 540 + VAT 27 → 567 (same as PDP). Order 567 + 27 is not a VAT pair.
  if (fromLineUnit != null && rawFromLineUnit != null && fromLineUnit > rawFromLineUnit + 0.01) {
    return fromLineUnit;
  }
  if (catalogList > 0.01 && rawCatalogList > 0.01 && catalogList > rawCatalogList + 0.01) {
    return catalogList;
  }
  if (fromLineUnit != null && fromLineUnit > 0) {
    // No line markdown — charged unit is already tax-inclusive retail (matches order subtotal).
    if (currentUnit != null && Math.abs(fromLineUnit - currentUnit) <= 0.02) {
      return fromLineUnit;
    }

    if (currentUnit == null || Math.abs(fromLineUnit - currentUnit) > 0.02) {
      const grossedUp = toVatInclusiveCatalogAmount(
        grossUpCheckoutLineUnitPrice(cartItem, fromLineUnit),
        paidTax,
      );
      const checkoutListPerUnit =
        undiscountedSubtotalGross != null && undiscountedSubtotalGross > 0
          ? undiscountedSubtotalGross / quantity
          : null;
      // Never adopt checkout undiscountedSubtotal when it is inflated by BXGY free-gift retail
      // (e.g. paid 603.75 + gift 1002.75 = 1606.50 on a single merchandise line).
      if (
        checkoutListPerUnit != null &&
        checkoutMerchandiseLineCount === 1 &&
        checkoutListPerUnit > grossedUp + 0.02 &&
        checkoutListPerUnit <= grossedUp * 1.15 + 0.02
      ) {
        return preferVatInclusiveAmount(grossedUp, checkoutListPerUnit);
      }
      return grossedUp;
    }
  }

  const fromLineTotal = getLineUndiscountedTaxedGrossAmount(
    cartItem?.undiscountedTotalPrice as Price | { amount?: number | null } | null,
  );
  if (fromLineTotal != null && fromLineTotal > 0) {
    return toVatInclusiveCatalogAmount(fromLineTotal / quantity, paidTax);
  }

  const pricing = cartItem?.variant?.pricing;

  // Prefer Saleor TaxedMoney (has net/gross/tax) before any FE tax-ratio invention.
  const fromPriceUndiscounted = toVatInclusiveCatalogAmount(
    resolveTaxedGrossAmount(pricing?.priceUndiscounted),
    paidTax,
  );
  if (fromPriceUndiscounted > 0) return fromPriceUndiscounted;

  const undiscountedNetLike = pricing?.priceUndiscounted?.gross?.amount;
  const paidGross = pricing?.price?.gross?.amount;

  // Fallback only when TaxedMoney net/tax is missing: gross-up net-like catalogue amounts only.
  if (undiscountedNetLike != null && paidGross != null && paidTax > 0) {
    const retailGross = applyTaxRatioIfNetLike(undiscountedNetLike, paidGross, paidTax);
    if (retailGross > undiscountedNetLike + 0.02) return retailGross;
    if (retailGross > 0) return retailGross;
  }

  const fromPrice = resolveTaxedGrossAmount(pricing?.price);
  if (fromPrice > 0) return fromPrice;

  if (
    undiscountedSubtotalGross != null &&
    undiscountedSubtotalGross > 0 &&
    checkoutMerchandiseLineCount === 1
  ) {
    const paidSubtotalPerUnit =
      paidSubtotalGross != null && paidSubtotalGross > 0 ? paidSubtotalGross / quantity : null;
    const subtotalLooksPostCoupon =
      paidSubtotalPerUnit != null &&
      currentUnit != null &&
      currentUnit > 0.01 &&
      Math.abs(currentUnit - paidSubtotalPerUnit) <= 0.02 &&
      undiscountedSubtotalGross > paidSubtotalGross! + 0.02;

    if (
      paidSubtotalPerUnit != null &&
      undiscountedSubtotalGross > paidSubtotalGross! + 0.02 &&
      !subtotalLooksPostCoupon
    ) {
      // Undiscounted checkout subtotal includes BXGY gift value — paid subtotal is merchandise retail.
      return paidSubtotalPerUnit;
    }

    if (!subtotalLooksPostCoupon) {
      return preferVatInclusiveAmount(
        fromPriceUndiscounted || fromLineUnit || 0,
        undiscountedSubtotalGross / quantity,
      );
    }
  }

  if (
    paidSubtotalGross != null &&
    paidSubtotalGross > 0 &&
    checkoutMerchandiseLineCount === 1 &&
    (currentUnit == null || currentUnit <= 0.01 || Math.abs(currentUnit * quantity - paidSubtotalGross) > 0.02)
  ) {
    return paidSubtotalGross / quantity;
  }

  return getCheckoutPriceAmount(cartItem?.unitPrice) ?? 0;
};

/** Sum tax-inclusive retail prices for paid merchandise lines (coupon subtotal baseline). */
export const sumMerchandiseRetailSubtotal = (
  checkoutLines?: CheckoutLines[] | null,
  undiscountedSubtotalGross?: number | null,
  paidSubtotalGross?: number | null,
): number => {
  // Never include BXGY / free-gift lines — checkout undiscountedSubtotal often does.
  const merchandiseLines = (checkoutLines ?? []).filter(
    (line) => isPaidCheckoutMerchandiseLine(line) && !isCheckoutGiftLine(line),
  );
  const lineCount = merchandiseLines.length;

  return merchandiseLines.reduce((sum, line) => {
    const unit = getLineRetailUnitPrice(line, {
      undiscountedSubtotalGross,
      checkoutMerchandiseLineCount: lineCount,
      paidSubtotalGross,
    });
    return sum + unit * (line.quantity || 1);
  }, 0);
};

const isPaidMerchandiseLine = (line: CheckoutLines): boolean =>
  isPaidCheckoutMerchandiseLine(line);

/** Restore line unit price before voucher — coupon discount belongs in order summary only. */
export const getLinePreCouponUnitPrice = (
  line: CheckoutLines,
  voucherDiscountAmount: number,
  checkoutLines?: CheckoutLines[] | null,
  options?: {
    undiscountedSubtotalGross?: number | null;
    checkoutMerchandiseLineCount?: number;
  },
): number => {
  if (isCheckoutGiftLine(line) && !isPaidCheckoutMerchandiseLine(line)) {
    return getCheckoutPriceAmount(line?.unitPrice) ?? 0;
  }

  const quantity = line?.quantity || 1;
  let currentUnit =
    getCheckoutPriceAmount(line?.unitPrice) ??
    (getCheckoutPriceAmount(line?.totalPrice) ?? 0) / quantity;

  const retailUnitPrice = getLineRetailUnitPrice(line, options);
  const undiscountedUnit = getCheckoutPriceAmount(line?.undiscountedUnitPrice);

  if (currentUnit <= 0.01) {
    currentUnit = undiscountedUnit ?? retailUnitPrice ?? currentUnit;
  }

  if (!voucherDiscountAmount || voucherDiscountAmount <= 0) {
    return currentUnit > 0.01 ? currentUnit : retailUnitPrice;
  }

  const allocatableLines = (checkoutLines ?? []).filter(isPaidMerchandiseLine);
  const totalAllocatableUnits = allocatableLines.reduce((sum, item) => sum + (item.quantity || 1), 0);

  if (totalAllocatableUnits <= 0 || !allocatableLines.some((item) => item.id === line.id)) {
    return currentUnit > 0.01 ? currentUnit : retailUnitPrice;
  }

  const postCouponUnit = getCheckoutPriceAmount(line?.unitPrice) ?? currentUnit;
  if (postCouponUnit <= 0.01 && retailUnitPrice > 0.01) {
    return retailUnitPrice;
  }

  const restored = postCouponUnit + voucherDiscountAmount / totalAllocatableUnits;
  // AE MLC-1986: 782 + 100 = 882. Catalog list is 840 — never display taxed gross as list.
  if (retailUnitPrice > 0.01 && restored > retailUnitPrice + 0.01) {
    return retailUnitPrice;
  }
  return restored;
};

const isFixedPromotionReward = (promotion?: AppliedPromotionsType | null): boolean =>
  promotion?.rewardValueType === "FIXED" || promotion?.rewardValueType === "Fixed";

/** Catalogue promotion eligible for product-card display (not limit-blocked). */
export const getEligibleCataloguePromotionForCard = (
  appliedPromotions?: AppliedPromotionsType[] | null,
): AppliedPromotionsType | null => {
  const [applied] = appliedPromotions || [];
  if (
    applied &&
    !isPromotionLimitReached(applied) &&
    applied.promotion?.type !== "BUYXGETY" &&
    applied.promotion?.type !== "ORDER"
  ) {
    return applied;
  }
  return null;
};

/** Catalogue sale unit price from retail and promotion reward — excludes coupon. */
export const getCataloguePromotionUnitPrice = (
  retailUnitPrice: number,
  promotion?: AppliedPromotionsType | null,
  fallbackFixedAmount?: number | null,
): number => {
  if (!promotion || retailUnitPrice <= 0) return retailUnitPrice;
  const reward = promotion.rewardValue ?? 0;
  const fixedFallback = fallbackFixedAmount ?? 0;
  if (reward <= 0 && fixedFallback <= 0.01) return retailUnitPrice;
  if (isFixedPromotionReward(promotion) || (reward <= 0 && fixedFallback > 0.01)) {
    return Math.max(retailUnitPrice - (reward > 0 ? reward : fixedFallback), 0);
  }
  return retailUnitPrice * (1 - reward / 100);
};

/**
 * Product-card unit price.
 * Catalogue-only: list − catalogue reward. Catalogue + coupon: Saleor payable
 * (unitPrice / pricing.price), so the card matches Item Total / Order Total.
 */
export const getLineProductCardUnitPrice = (
  cartItem?: CheckoutLines | null,
  options?: {
    voucherCode?: string | null;
    undiscountedSubtotalGross?: number | null;
    checkoutMerchandiseLineCount?: number;
    paidSubtotalGross?: number | null;
    voucherDiscountAmount?: number;
    checkoutLines?: CheckoutLines[] | null;
    appliedPromotions?: AppliedPromotionsType[] | null;
    availablePromotions?: AppliedPromotionsType[] | null;
  },
): number => {
  const {
    voucherCode,
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
    paidSubtotalGross,
    voucherDiscountAmount,
    checkoutLines,
    appliedPromotions,
    availablePromotions,
  } = options ?? {};

  const retailUnitPrice = getLineRetailUnitPrice(cartItem, {
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
    paidSubtotalGross,
  });

  if (
    shouldUseUndiscountedPromotionPrice(appliedPromotions, availablePromotions, !!voucherCode) &&
    !voucherCode
  ) {
    return retailUnitPrice;
  }

  // Per-code + voucher: skip catalogue/BXGY sale — card shows list − coupon strikethrough.
  const couponBlocked = isAnyPromotionBlockedByCoupon(
    appliedPromotions,
    availablePromotions,
    !!voucherCode,
  );
  const eligiblePromotion = couponBlocked
    ? null
    : getEligibleCataloguePromotionForCard(appliedPromotions);
  if (eligiblePromotion) {
    const catalogueUnit = getCataloguePromotionUnitPrice(
      retailUnitPrice,
      eligiblePromotion,
      cartItem?.variant?.pricing?.discount?.gross?.amount,
    );
    if (voucherCode) {
      const variantSale = resolveTaxedGrossAmount(cartItem?.variant?.pricing?.price);
      const lineUnit = getCheckoutPriceAmount(cartItem?.unitPrice) ?? 0;
      const payableUnit = variantSale > 0.01 ? variantSale : lineUnit;
      if (payableUnit > 0.01 && payableUnit < catalogueUnit - 0.01) {
        return payableUnit;
      }
    }
    return catalogueUnit;
  }

  if (voucherCode) {
    const patched = getLineCatalogVoucherPaidUnitPrice(cartItem, {
      undiscountedSubtotalGross,
      checkoutMerchandiseLineCount,
      paidSubtotalGross,
      voucherDiscountAmount,
      checkoutLines,
    });
    if (patched != null) {
      return patched;
    }
  }

  const variantSale = resolveTaxedGrossAmount(cartItem?.variant?.pricing?.price);
  if (variantSale > 0.01 && variantSale < retailUnitPrice - 0.01) {
    return variantSale;
  }

  // BXGY / line-level promo sale (before coupon). With voucher, prefer patched / variant sale above.
  if (!voucherCode) {
    const lineUnit = getCheckoutPriceAmount(cartItem?.unitPrice) ?? 0;
    if (lineUnit > 0.01 && lineUnit < retailUnitPrice - 0.01) {
      return lineUnit;
    }
  }

  return retailUnitPrice;
};
